/**
 * DeckViewToggle - Toggle between list, grid, and stack view modes
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import { useAtom } from 'jotai';
import { FaAlignLeft, FaGripHorizontal, FaLayerGroup, FaList } from 'react-icons/fa';

import { deckViewModeAtom, type DeckViewMode } from '../state/deckEditorSettings';

export interface DeckViewToggleProps {
  className?: string;
}

interface ViewOption {
  mode: DeckViewMode;
  icon: ReactElement;
  label: string;
}

const VIEW_OPTIONS: ViewOption[] = [
  { mode: 'list', icon: <FaList className="h-3.5 w-3.5" />, label: 'List view' },
  { mode: 'grid', icon: <FaGripHorizontal className="h-3.5 w-3.5" />, label: 'Grid view' },
  { mode: 'stack', icon: <FaLayerGroup className="h-3.5 w-3.5" />, label: 'Stack view' },
  { mode: 'summary', icon: <FaAlignLeft className="h-3.5 w-3.5" />, label: 'Summary view' },
];

export default function DeckViewToggle({ className }: DeckViewToggleProps): ReactElement {
  const [viewMode, setViewMode] = useAtom(deckViewModeAtom);

  return (
    <div className={clsx('inline-flex rounded-lg border border-zinc-700 bg-zinc-800/50 p-0.5', className)}>
      {VIEW_OPTIONS.map(({ mode, icon, label }) => (
        <button
          key={mode}
          type="button"
          onClick={() => setViewMode(mode)}
          className={clsx(
            'flex items-center justify-center rounded-md px-3 py-1.5 transition-all',
            viewMode === mode ? 'bg-zinc-700 text-zinc-100 shadow-sm' : 'text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200',
          )}
          aria-label={label}
          aria-pressed={viewMode === mode}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}
